import styles from "./SilenceModal.module.css"
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setCloseSilenceRules, setEditRule, setSilenceRules } from "../../store/reducers/silenceRulesReducer";
import AlertService from "../../services/AlertService";
import { setModalError } from "../../store/reducers/modalReducer";
import ReactDOM from "react-dom";
import ReactLoading from "react-loading";
import { SILENCED_RULES_DISPLAY } from "../../store/actions/DISPLAY_ACTIONS";
import { DEFAULT_SILENCE_DURATION, formatSilenceEnd, parseSilenceDuration } from "../../utils/silenceDuration";

function SilenceModal() {
    const rulesPortal = document.getElementById("rulesPortal");
    const dispatch = useDispatch();
    const isOpenSilenceModal = useSelector(state => state.setSilenceRules.isOpen)
    const silenceRule = useSelector(state => state.setSilenceRules.editRule)
    const displayMode = useSelector(state => state.setDisplay.display)
    const projects = useSelector(state => state.authReducer.user.userProjects)
    const activeProjects = projects.filter((project) => project !== "All")
    const [ruleProject, setRuleProject] = useState("")
    const [ruleHost, setRuleHost] = useState("")
    const [ruleAlert, setRuleAlert] = useState("")
    const [ruleDuration, setRuleDuration] = useState("")
    const [ruleComment, setRuleComment] = useState("")
    const [isDisplayInNormal, setIsDisplayInNormal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [position, setPosition] = useState({
        x: (window.innerWidth - 500) / 2,
        y: (window.innerHeight - 600) / 2
    });
    const [isDragging, setIsDragging] = useState(false);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [projectsOpen, setProjectsOpen] = useState(false);
    const durationResult = useMemo(() => parseSilenceDuration(ruleDuration), [ruleDuration])

    useEffect(() => {
        setRuleProject(silenceRule.project)
        setRuleHost(silenceRule.host)
        setRuleAlert(silenceRule.alertName)
        setRuleDuration(silenceRule._id ? silenceRule.duration : DEFAULT_SILENCE_DURATION)
        setRuleComment(silenceRule.comment)
        const hasDisplayLabel = silenceRule?.labels?.display_in_normal === true
        setIsDisplayInNormal(hasDisplayLabel)
    }, [silenceRule])

    const onMouseDown = (e) => {
        if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "BUTTON") {
            return;
        }
        setIsDragging(true);
        setOffset({
            x: e.clientX - position.x,
            y: e.clientY - position.y
        });
    };

    const onMouseMove = (e) => {
        if (isDragging) {
            setPosition({
                x: e.clientX - offset.x,
                y: e.clientY - offset.y
            });
        }
    };

    const onMouseUp = () => {
        setIsDragging(false);
    };

    useEffect(() => {
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);

        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, [isDragging]);


    const refreshRules = async () => {
        const newRules = []
        try {
            const response = await AlertService.getSileneced()
            response.data.forEach((rule) => {
                newRules.push(rule)
            })
        }
        catch (e) {
            dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
        }
        dispatch(setSilenceRules(newRules))
    }

    const emptyRule = {
        _id: null,
        project: "",
        host: "",
        alertName: "",
        duration: "",
        endSilence: null,
        comment: "",
        labels: {},
    }

    const onCloseHandler = () => {
        dispatch(setEditRule(emptyRule))
        dispatch(setCloseSilenceRules())
        setPosition({
            x: (window.innerWidth - 500) / 2,
            y: (window.innerHeight - 600) / 2
        })
    }

    const closeByEsc = (e) => {
        if (e.key === 'Escape') {
            onCloseHandler()
        }
    }

    useEffect(() => {
        document.addEventListener("keydown", closeByEsc)
        return () => {
            document.removeEventListener("keydown", closeByEsc)
        }
    }, [isOpenSilenceModal]);

    const onSaveRuleHandler = async () => {
        if (!durationResult.isValid) {
            dispatch(setModalError(durationResult.error))
            return
        }

        if (silenceRule._id) {
            setIsLoading(true)
            try {
                await AlertService.unsilence([{ silenceId: silenceRule._id }])
            }
            catch (error) {
                setIsLoading(false)
                dispatch(setCloseSilenceRules())
                dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
            }
        }
        const newRule = {
            project: ruleProject !== "All" ? ruleProject : "",
            host: ruleHost,
            alertName: ruleAlert,
            startSilence: Date.now(),
            endSilence: parseSilenceDuration(ruleDuration).endSilence,
            comment: ruleComment,
        }

        const labels =
            silenceRule?.labels && typeof silenceRule.labels === "object" && !Array.isArray(silenceRule.labels)
                ? { ...silenceRule.labels }
                : {}

        if (isDisplayInNormal) {
            labels.display_in_normal = true
        } else {
            delete labels.display_in_normal
        }

        if (silenceRule._id || Object.keys(labels).length > 0) {
            newRule.labels = labels
        }

        try {
            await AlertService.silence(newRule)
        }
        catch (error) {
            setIsLoading(false)
            dispatch(setCloseSilenceRules())
            dispatch(setModalError("Oops. Something went wrong. Please, try a bit later"))
        }

        if (displayMode === SILENCED_RULES_DISPLAY) {
            refreshRules()
        }

        dispatch(setEditRule(emptyRule))
        dispatch(setCloseSilenceRules())
        setIsLoading(false)
    }

    const onProjectButtonClick = (e) => {
        e.preventDefault()
        setProjectsOpen(!projectsOpen)
    }

    const onProjectSelectClick = (project) => {
        setRuleProject(project)
        setProjectsOpen(false)
    }


    if (!rulesPortal) {
        return null
    }

    if (!isOpenSilenceModal) {
        return null
    }

    return ReactDOM.createPortal((
        <div className={styles.modalOverlay}>
            <div className={styles.modalWindow}
                style={{
                    position: "absolute",
                    left: `${position.x}px`,
                    top: `${position.y}px`,
                    cursor: isDragging ? "grabbing" : "grab"
                }}
                onMouseDown={onMouseDown}
                onClick={e => e.stopPropagation()}>

                <button className={styles.closeButton}
                    onClick={(e) => {
                        e.preventDefault()
                        onCloseHandler()
                    }}
                />
                <form className={styles.modalForm} >
                    <div className={styles.projectSection}>
                        <button className={` ${styles.projectButton} ${styles.inputField} ${ruleProject ? styles.projectButtonActive : null} ${projectsOpen ? styles.projectButtonOpened : styles.projectButtonClosed}`} onClick={e => onProjectButtonClick(e)}> {ruleProject ? ruleProject : "Project"}</button>
                        <div className={`${projectsOpen ? styles.projectDropdownSection : styles.projectDropdownClosed}`}>
                            {
                                activeProjects.map((item) => (
                                    <button key={item} className={styles.projectSelectionButton}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            onProjectSelectClick(item)
                                        }}
                                    >
                                        {item}
                                    </button>
                                ))
                            }
                        </div>
                    </div>
                    <input type="text" placeholder="Host" className={styles.inputField} value={ruleHost} onChange={(e) => setRuleHost(e.target.value)} />
                    <input type="text" placeholder="Alert Name" className={styles.inputField} value={ruleAlert} onChange={(e) => setRuleAlert(e.target.value)} />
                    <input
                        type="text"
                        placeholder="30m, 2h, 1 day"
                        className={`${styles.inputField} ${durationResult.error ? styles.inputFieldInvalid : ''}`}
                        value={ruleDuration}
                        title={durationResult.error || 'Use values like 30m, 2h, 1 day, 1 month'}
                        onChange={(e) => setRuleDuration(e.target.value)}
                    />
                    {durationResult.error ? <p className={styles.validationError}>{durationResult.error}</p> : null}
                    {!durationResult.error && ruleDuration.trim() ? <p className={styles.expectedEnd}>Until {formatSilenceEnd(parseSilenceDuration(ruleDuration).endSilence)}</p> : null}
                    <input type="text" placeholder="Comment" className={styles.inputField} value={ruleComment} onChange={(e) => setRuleComment(e.target.value)} />
                    <div className={styles.displayCheckboxBlock}>
                        <input
                            type="checkbox"
                            checked={isDisplayInNormal}
                            onChange={(e) => setIsDisplayInNormal(e.target.checked)}
                            className={styles.checkbox}
                        />
                        <p className={styles.displayCheckboxTitle}>display in normal</p>
                    </div>
                    <button type="submit" className={`${ruleComment && ruleComment.length > 3 && ruleProject && durationResult.isValid ? styles.editButton : styles.ediButtonDisabled}`}
                        disabled={!ruleComment || !ruleProject || !durationResult.isValid}
                        onClick={(e) => {
                            e.preventDefault();
                            onSaveRuleHandler()
                        }}
                    >Save</button>
                </form>
            </div>
            {
                isLoading
                    ?
                    <div className={styles.loadOverlay}>
                        <ReactLoading color={'#01A2D8'} type={"spin"} height={200} width={100} />
                    </div>
                    :
                    <></>
            }
        </div>
    ), rulesPortal)
}

export default SilenceModal
